var AbstractTracker = require("app/Analytics/AbstractTracker"),
    config          = require("app/config"),
    deepEqual       = require("fast-deep-equal"),
    get             = require("object-path").get,
    kind            = require("enyo/kind");

/**
 * GoogleTagManager script.
 */
module.exports = kind(/** @lends  app.Analytics.GoogleTagManager.prototype */ {

    /**
     * @private
     */
    name: "app.Analytics.GoogleTagManager",

    /**
     * @private
     */
    kind: AbstractTracker,

    /**
     * Whether the tracker is form an IAB Vendor.
     */
    isIABVendor: true,

    /**
     * Google also has Non-IAB consent settings.
     */
    isNonIABVendor: true,

    /**
     * Cached copy of consent data, used to detect consent changes.
     *
     * @private
     */
    consent: null,

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.consent = {
                iabConsentData:    null,
                nonIABConsentData: null
            };
            sup.apply(this, arguments);
        };
    }),

    // Vendor Transparency and Consent
    // ----------------

    /**
     * Respond to changes in IAB Consent Data.
     *
     * @abstract
     * @protected
     */
    onIABConsentDataChange: function() {
        this.onConsentDataChange();
    },

    /**
     * Respond to changes in Non-IAB Consent Data.
     *
     * @abstract
     * @protected
     */
    onNonIABConsentDataChange: function() {
        this.updateGoogleConsentMode();
        this.onConsentDataChange();
    },

    /**
     * Respond to changes in consent.
     *
     * @private
     */
    onConsentDataChange: function() {
        var $privacyService = this.app.$.privacyService,
            consent         = {
                iabConsentData:    $privacyService.get("iabConsentData"),
                nonIABConsentData: $privacyService.get("nonIABConsentData")
            },
            getTcString;

        getTcString = function(iabConsentData) {
            if (iabConsentData && iabConsentData.tcString) {
                return iabConsentData.tcString;
            }
            else {
                return null;
            }
        };

        // Non-IAB consent changed
        if (!deepEqual(this.consent.nonIABConsentData, consent.nonIABConsentData)) {
            this.consent = consent;
        }

        // IAB consent changed
        else if (getTcString(this.consent.iabConsentData) !== getTcString(consent.iabConsentData)) {
            this.consent = consent;
        }

        // Consent unchanged, we can bail early
        else {
            return;
        }

        // We are not responsible for loading ourselves
        if (get(config, "googleTagManager.loader", null) !== "self") {
            return;
        }

        // We aren't ready yet, but are allowed to load.
        if (!this.isReady && this.hasLoadConsent()) {
            this.load();
        }

        // We are ready, but consent has just been revoked.
        else if (this.isReady && !this.hasLoadConsent()) {
            this.app.$.router.reload();
        }
    },

    // Loading
    // ----------------

    /**
     * @returns  {boolean}  Whether we have some form of TCF consent
     * @private
     */
    hasLoadConsent: function() {
        var $privacyService   = this.app.$.privacyService,
            nonIABConsentData = this.consent.nonIABConsentData,
            iabConsentData    = this.consent.iabConsentData;

        // Bypass parameter set
        if ($privacyService.shouldBypassConsent()) {
            return true;
        }

        // No Non-IAB Consent
        if (!nonIABConsentData
            || !nonIABConsentData.accepted
            || !Array.isArray(nonIABConsentData.accepted)
            || !(
                nonIABConsentData.accepted.includes("analytics")
                || nonIABConsentData.accepted.includes("advertisement")
                || nonIABConsentData.accepted.includes("functional")
                || nonIABConsentData.accepted.includes("necessary")
            )
        ) {
            return false;
        }

        // IAB Consent Required
        if ($privacyService.isIabEnabled()

            // IMPORTANT: If bypassing IAB here, GA should still be configured
            // to respect IAB consent by using the TCF API. In order to
            // facilitate this we need to at minimum set the
            // `gtag_enable_tcf_support` flag to true when loading and
            // configuring GA.
            && !get(config, "googleTagManager.bypassIAB", false)) {
            if (!iabConsentData
                || !iabConsentData.vendor

                // Google's TCFv2 Vendor ID is 755. Source:
                // https://iabeurope.eu/vendor-list-tcf-v2-0/
                || !(iabConsentData.vendor.consents && iabConsentData.vendor.consents[755])
                || !(iabConsentData.vendor.legitimateInterests && iabConsentData.vendor.legitimateInterests[755])
            ) {
                return false;
            }
        }

        // Consent checks passed
        return true;
    },

    /**
     * @return {string|null} The loader
     * @public
     */
    getLoader: function() {
        return get(config, "googleTagManager.loader", null);
    },

    /**
     * @private
     */
    load: function() {
        var $privacyService   = this.app.$.privacyService,
            nonIABConsentData = this.consent.nonIABConsentData,
            purposes,
            url,
            script;

        // Google Tag Manager not enabled
        if (!get(config, "googleTagManager.enabled", false)
            || get(config, "googleTagManager.loader", null) !== "self"
            || !get(config, "googleTagManager.id", false)) {
            return;
        }

        // Bail if already loaded
        if (this.isReady) {
            return;
        }

        // Prepare dataLayer
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            "gtm.start": new Date().getTime(),
            event:       "gtm.js"
        });

        if (!$privacyService.shouldBypassConsent()) {
            // TCF support flag for integration with our iabConsentData. Never
            // remove this flag for platforms that require IAB consent, because
            // without it, GTAG cannot be configured to properly respect IAB
            // consent settings.
            window.gtag_enable_tcf_support = true;

            // Recommended by CookieYes: https://www.cookieyes.com/documentation/implementing-google-consent-mode-using-cookieyes/
            purposes = nonIABConsentData
                ? nonIABConsentData.accepted || []
                : [];
            window.dataLayer.push(["consent", "default", {
                ad_storage:              purposes.includes("advertisement") ? "granted" : "denied",
                ad_user_data:            purposes.includes("advertisement") ? "granted" : "denied",
                ad_personalization:      purposes.includes("advertisement") ? "granted" : "denied",
                analytics_storage:       purposes.includes("analytics") ? "granted" : "denied",
                functionality_storage:   purposes.includes("functional") ? "granted" : "denied",
                personalization_storage: purposes.includes("functional") ? "granted" : "denied",
                security_storage:        purposes.includes("necessary") ? "granted" : "denied",
                wait_for_update:         2000
            }]);
            window.dataLayer.push(["set", "ads_data_redaction", true]);
            window.dataLayer.push(["set", "url_passthrough", true]);
        }

        // Build url
        url = new URL("https://www.googletagmanager.com/gtm.js");
        url.searchParams.set("id", get(config, "googleTagManager.id", null));
        url.searchParams.set("l", "dataLayer");

        // Load script
        script         = document.createElement("script");
        script.async   = true;
        script.src     = url.toString();
        script.onload  = this.onLoad;
        script.onerror = this.onLoadError;
        document.head.appendChild(script);
    },

    /**
     * @returns  {boolean}  `true` when ready, `false` otherwise.
     * @private
     */
    verifyReadiness: function() {
        var id = get(config, "googleAnalytics.id", false);

        return Boolean(get(window, "google_tag_manager." + id, null));
    },

    /**
     * @private
     */
    onReady: kind.inherit(function(sup) {
        return function() {
            var $analyticsService = this.app.$.analyticsService,
                services          = get(config, "googleTagManager.services", []);

            // Consent has been revoked while we were getting ready
            if (get(config, "googleTagManager.loader", null) === "self"
                && !this.hasLoadConsent()) {
                this.app.$.router.reload();

                return;
            }

            // Make sure Google Consent Mode settings are up-to-date
            this.updateGoogleConsentMode();

            sup.apply(this, arguments);

            // Tell the AnalyticsServices we're done loading so it can activate
            // the services we've loaded.
            services = Array.isArray(services) ? services : [];
            $analyticsService.onLoaderReady("googleTagManager", services);
        };
    }),

    /**
     * Send updated Google Consent Mode v2 settings to GA4.
     *
     * @private
     */
    updateGoogleConsentMode: function() {
        var $privacyService   = this.app.$.privacyService,
            nonIABConsentData = $privacyService.get("nonIABConsentData"),
            purposes;

        // Push NonIAB consent changes to GTM
        if (window.dataLayer) {
            purposes = nonIABConsentData
                ? nonIABConsentData.accepted || []
                : [];

            window.dataLayer.push(["consent", "update", {
                ad_storage:              purposes.includes("advertisement") ? "granted" : "denied",
                ad_user_data:            purposes.includes("advertisement") ? "granted" : "denied",
                ad_personalization:      purposes.includes("advertisement") ? "granted" : "denied",
                analytics_storage:       purposes.includes("analytics") ? "granted" : "denied",
                functionality_storage:   purposes.includes("functional") ? "granted" : "denied",
                personalization_storage: purposes.includes("functional") ? "granted" : "denied",
                security_storage:        purposes.includes("necessary") ? "granted" : "denied"
            }]);
        }
    }

});
